import { ActionKind } from '@dynamic/ui';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useActions, useNavigation } from '#/hooks';
import { CommonAction } from '#/service/config';
import { remify } from '#/service/utils';

import { Logo, LogoMvpd } from '../Logo';
import { MenuGradient, MenuNav } from './components';
import { ItemLink } from './ItemLink';
import { getDefaultMenuItemId, isLoginAction, MenuProps } from './utils';

/** Menu bar component to show app logo and menu items. */
export const Menu: FC<MenuProps> = ({
  mainMenuItems = [],
  linkMenuItems = [],
  isCollapsed = false,
  isHidden = false,
}) => {
  const { actions } = useActions();
  const { goHome } = useNavigation('Menu');
  const { pathname } = useLocation();

  const showIcons = true;

  // Hide the menu on the playback page.
  if (pathname.startsWith('/playback')) {
    return null;
  }

  // If there is a login button in the main menu, we must make it open login popup.
  // there is no page to route to
  const getCallback = (action: CommonAction) => {
    if (isLoginAction(action)) {
      actions[ActionKind.Login]();
    }
  };

  const defaultSelectedItem = getDefaultMenuItemId(mainMenuItems);

  return (
    <MenuNav $isCollapsed={isCollapsed} $isHidden={isHidden}>
      <MenuGradient />
      <MenuLogo onClick={goHome} />
      {mainMenuItems.map((item) => (
        <ItemLink
          defaultSelectedItem={defaultSelectedItem}
          item={item}
          key={item.id}
        />
      ))}
      {/*
      `data-nosnippet` is to exclude cog menu items from being indexed by Google.
      That's a temporary solution and it's because menu items are not links, but
      just a text from the crawler's perspective
       */}
      <RightSide data-nosnippet>
        <LogoMvpd />
        {linkMenuItems.map((item) => (
          <ItemLink
            defaultSelectedItem={defaultSelectedItem}
            item={item}
            key={item.id}
            onClick={() => getCallback(item?.action)}
            showIcons={showIcons}
          />
        ))}
      </RightSide>
    </MenuNav>
  );
};

const MenuLogo = styled(Logo)`
  padding: 0 ${remify(50)};
`;

MenuLogo.displayName = 'MenuLogo';

const RightSide = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  height: ${remify(80)};
  padding: 0 ${remify(40)} 0 ${remify(20)};

  & > * {
    margin-left: 1rem;
  }

  & a,
  button {
    padding: 0 ${remify(10)};
  }
`;

RightSide.displayName = 'RightSide';
